import { useAuth } from '@/contexts/AuthContext';
import { Package, Truck, Clock, CheckCircle, Plus, Bell, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { StatsGrid } from '@/components/dashboard/StatsGrid';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { DashboardStat } from '@/types/dashboard';
import { useOrders } from '@/hooks/useOrders';
import { OrderCard } from '@/components/orders/OrderCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useShipperJobs } from '@/hooks/useShipperJobs';
import { PendingReservationCard } from '@/components/shipper/PendingReservationCard';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useShipperBids } from '@/hooks/useShipperBids';
import { BidCard } from '@/components/shipper/BidCard';

const ShipperDashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orders, isLoading } = useOrders();
  const { pendingJobs, isLoading: loadingJobs, confirmJob, isConfirming, declineJob, isDeclining } = useShipperJobs();
  const { pendingBids, isLoading: loadingBids, acceptBid, declineBid, isAccepting, isDeclining: isDecliningBid } = useShipperBids();

  const handleConfirmJob = async (jobId: string) => {
    try {
      await confirmJob(jobId);
      toast.success(t('shipper.messages.reservationConfirmed'));
    } catch (error: any) {
      toast.error(error.message || t('shipper.errors.confirmError'));
    }
  };

  const handleDeclineJob = async (jobId: string) => {
    try {
      await declineJob(jobId);
      toast.success(t('shipper.messages.reservationDeclined'));
    } catch (error: any) {
      toast.error(error.message || t('shipper.errors.declineError'));
    }
  };

  const handleAcceptBid = async (bidId: string) => {
    try {
      await acceptBid(bidId);
    } catch (error: any) {
      // Error is already handled in the hook
    }
  };

  const handleDeclineBid = async (bidId: string) => {
    try {
      await declineBid(bidId);
    } catch (error: any) {
      // Error is already handled in the hook
    }
  };

  const draftOrders = orders.filter(o => o.status === 'draft').length;
  const activeOrders = orders.filter(o => ['posted', 'booked', 'assigned', 'in_transit'].includes(o.status)).length;
  const deliveredOrders = orders.filter(o => o.status === 'delivered').length;

  const stats: DashboardStat[] = [
    {
      title: t('shipper.stats.totalOrders'),
      value: orders.length.toString(),
      icon: Package,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      title: t('shipper.stats.inProgress'),
      value: activeOrders.toString(),
      icon: Truck,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: t('shipper.stats.delivered'),
      value: deliveredOrders.toString(),
      icon: CheckCircle,
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      title: t('shipper.stats.drafts'),
      value: draftOrders.toString(),
      icon: Clock,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
    },
  ];

  // Only show orders in progress (assigned and in_transit)
  const ordersInProgress = orders.filter(o => ['assigned', 'in_transit'].includes(o.status));

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <DashboardHeader 
          title={t('shipper.dashboard.title')}
          userName={user?.name || user?.email}
        />
        <Button 
          onClick={() => navigate('/shipper/orders/create')}
          size="lg"
          className="gap-2"
        >
          <Plus size={20} />
          {t('shipper.dashboard.createOrder')}
        </Button>
      </div>

      <StatsGrid stats={stats} />

      {/* Pending Bids Section */}
      {pendingBids.length > 0 && (
        <Card className="rounded-xl shadow-md border-2 border-[#0880AE]">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="text-[#0880AE]" size={24} />
              <CardTitle className="text-xl font-medium text-[#37699E] font-heading">
                {t('shipper.dashboard.pendingBids')}
              </CardTitle>
            </div>
            <CardDescription>
              {t('shipper.dashboard.pendingBidsSubtitle', { count: pendingBids.length })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingBids ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-96 rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingBids.map((bid) => (
                  <BidCard
                    key={bid.id}
                    bid={bid}
                    onAccept={() => handleAcceptBid(bid.id)}
                    onDecline={() => handleDeclineBid(bid.id)}
                    isAccepting={isAccepting}
                    isDeclining={isDecliningBid}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Reservations Section */}
      {pendingJobs.length > 0 && (
        <Card className="rounded-xl shadow-md border-2 border-[#F2AC57]">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="text-[#F2AC57]" size={24} />
              <CardTitle className="text-xl font-medium text-[#37699E] font-heading">
                {t('shipper.dashboard.pendingReservations')}
              </CardTitle>
            </div>
            <CardDescription>
              {t('shipper.dashboard.pendingReservationsSubtitle', { count: pendingJobs.length })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingJobs ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-64 rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingJobs.map((job) => (
                  <PendingReservationCard
                    key={job.id}
                    job={job}
                    onConfirm={() => handleConfirmJob(job.id)}
                    onDecline={() => handleDeclineJob(job.id)}
                    isConfirming={isConfirming}
                    isDeclining={isDeclining}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-xl shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-medium text-secondary font-heading">
                {t('shipper.dashboard.ordersInProgress')}
              </CardTitle>
              <CardDescription>
                {t('shipper.dashboard.ordersInProgressSubtitle')}
              </CardDescription>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate('/shipper/orders')}
              className="bg-white border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] font-semibold rounded-lg transition-colors duration-200"
            >
              {t('shipper.dashboard.viewAllOrders')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 rounded-xl" />
              ))}
            </div>
          ) : ordersInProgress.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="mx-auto text-border-muted mb-4" size={48} />
              <p className="text-sm text-muted-foreground mb-4">
                {t('shipper.dashboard.noOrdersInProgress')}
              </p>
              <Button 
                variant="outline"
                onClick={() => navigate('/shipper/orders')}
                className="bg-white border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] font-semibold rounded-lg transition-colors duration-200"
              >
                {t('shipper.dashboard.viewAllOrders')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ordersInProgress.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShipperDashboard;
